import { useState, useEffect } from 'react';
import { faqService, testimonialService, blogService } from '../services/api';
import { FAQItem, TestimonialItem, BlogPost } from '../types/api';
import { mockBlogPosts, getMockBlogPost } from '../data/mockBlogData';

export const useFAQ = () => {
  const [faqData, setFaqData] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadFAQ = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await faqService.getAll();
        
        if (response.success && response.data && isMounted) {
          setFaqData(response.data);
        } else if (isMounted) {
          setError(response.error || 'Erro ao carregar FAQ');
          // Fallback para dados estáticos em caso de erro
          setFaqData([
            {
              id: 1,
              question: "Você sabia que",
              answer: "Muitos trabalhadores têm direitos que desconhecem. Horas extras não pagas, adicional noturno, férias proporcionais e 13º salário são apenas alguns exemplos.",
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            },
            {
              id: 2,
              question: "Conheça os tipos de danos morais no trabalho",
              answer: "Assédio moral, discriminação, constrangimento público, pressão psicológica e violação da intimidade são situações que podem gerar indenização por danos morais.",
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          ]);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Erro desconhecido');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadFAQ();
    
    return () => {
      isMounted = false;
    };
  }, []);

  return { faqData, loading, error };
};

export const useTestimonials = () => {
  const [testimonialsData, setTestimonialsData] = useState<TestimonialItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadTestimonials = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await testimonialService.getAll();
        
        if (response.success && response.data && isMounted) {
          setTestimonialsData(response.data);
        } else if (isMounted) {
          setError(response.error || 'Erro ao carregar Testimonials');
          // Fallback para dados estáticos em caso de erro
          setTestimonialsData([
            {
              id: 1,
              name: "Maria Silva",
              profession: "Assistente Administrativa",
              quote: "A Luara me explicou tudo com calma e resolveu meu caso com muita transparência.",
              rating: 5,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            },
            {
              id: 2,
              name: "João Santos",
              profession: "Técnico em Segurança",
              quote: "Me senti segura para decidir. Recomendo o atendimento humano da Luara.",
              rating: 5,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          ]);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Erro desconhecido');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadTestimonials();
    
    return () => {
      isMounted = false;
    };
  }, []);

  return { testimonialsData, loading, error };
};

export const useBlog = () => {
  const [blogData, setBlogData] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadBlog = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await blogService.getAll();
        
        if (response.success && response.data && isMounted) {
          const raw: any = response.data as any;
          const list: any[] = Array.isArray(raw)
            ? raw
            : (Array.isArray(raw?.rows) ? raw.rows : []);

          const normalized: BlogPost[] = list.map((p: any) => {
            let tags: string[] = [];
            if (Array.isArray(p.tags)) {
              tags = p.tags as string[];
            } else if (typeof p.tags === 'string') {
              try {
                const parsed = JSON.parse(p.tags);
                tags = Array.isArray(parsed) ? parsed : [];
              } catch {
                tags = [];
              }
            }

            const featuredBool = p.featured === true || p.featured === 1 || p.featured === '1';

            // Função para gerar slug se não existir
            const generateSlug = (title: string): string => {
              return title
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '') // Remove acentos
                .replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais
                .replace(/\s+/g, '-') // Substitui espaços por hífens
                .replace(/-+/g, '-') // Remove hífens duplicados
                .trim();
            };

            const title = p.title || '';
            const existingSlug = p.slug && String(p.slug).trim();
            const slug = existingSlug || generateSlug(title);

            return {
              id: Number(p.id),
              title,
              slug,
              excerpt: p.excerpt || '',
              content: p.content || '',
              author: p.author || 'Luara Rezende',
              date: p.date || new Date().toISOString(),
              read_time: p.read_time || '5 min',
              category: p.category || 'Direitos Trabalhistas',
              tags,
              featured: featuredBool,
              image: p.image || '/images/blog/placeholder.jpg',
              created_at: p.created_at || new Date().toISOString(),
              updated_at: p.updated_at || new Date().toISOString(),
            } as BlogPost;
          });

          setBlogData(normalized);
        } else if (isMounted) {
          setError(response.error || 'Erro ao carregar Blog');
          // Fallback para dados estáticos em caso de erro
          setBlogData(mockBlogPosts);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Erro desconhecido');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBlog();
    
    return () => {
      isMounted = false;
    };
  }, []);

  return { blogData, loading, error };
};

export const useArticle = (slug: string) => {
  const [article, setArticle] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadArticle = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await blogService.getBySlug(slug);
        console.log('🔍 Resposta da API para slug:', slug, response);
        
        if (response.success && response.data && isMounted) {
          // A API retorna um array, pegar o primeiro item
          const data = Array.isArray(response.data) ? response.data[0] : response.data;
          console.log('📄 Dados recebidos da API:', data);
          const p = data as any;
          
          let tags: string[] = [];
          if (Array.isArray(p.tags)) {
            tags = p.tags as string[];
          } else if (typeof p.tags === 'string') {
            try {
              // Tentar fazer parse JSON primeiro
              const parsed = JSON.parse(p.tags);
              if (Array.isArray(parsed)) {
                tags = parsed;
              } else {
                // Se não for array, tratar como string separada por vírgula
                tags = p.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag);
              }
            } catch {
              // Se não for JSON válido, tratar como string separada por vírgula
              tags = p.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag);
            }
          }
          console.log('🏷️ Tags normalizadas:', tags);

          const featuredBool = p.featured === true || p.featured === 1 || p.featured === '1';

          // Normalizar related_posts (pode vir como string JSON ou array)
          let relatedPosts: number[] = [];
          if (Array.isArray(p.related_posts)) {
            relatedPosts = p.related_posts;
          } else if (typeof p.related_posts === 'string') {
            try {
              const parsed = JSON.parse(p.related_posts);
              if (Array.isArray(parsed)) {
                relatedPosts = parsed;
              }
            } catch {
              // Se não for JSON válido, tentar como string separada por vírgula
              relatedPosts = p.related_posts.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id));
            }
          }

          const normalizedArticle: BlogPost = {
            id: Number(p.id),
            title: p.title || '',
            slug: (p.slug && String(p.slug).trim()) || '',
            excerpt: p.excerpt || '',
            content: p.content || '',
            author: p.author || 'Luara Rezende',
            date: p.date || new Date().toISOString(),
            read_time: p.read_time || '5 min',
            category: p.category || 'Direitos Trabalhistas',
            tags,
            featured: featuredBool,
            image: p.image || '/images/blog/placeholder.jpg',
            created_at: p.created_at || new Date().toISOString(),
            updated_at: p.updated_at || new Date().toISOString(),
            related_posts: relatedPosts,
          };

          console.log('📄 Artigo normalizado:', normalizedArticle);
          setArticle(normalizedArticle);
        } else if (isMounted) {
          setError(response.error || 'Artigo não encontrado');
          // Fallback para dados mock
          console.log('⚠️ API não disponível, usando dados mock...');
          const mockArticle = getMockBlogPost(slug);
          if (mockArticle) {
            setArticle(mockArticle);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Erro desconhecido');
          // Fallback para dados mock em caso de erro
          console.log('⚠️ API não disponível, usando dados mock...');
          const mockArticle = getMockBlogPost(slug);
          if (mockArticle) {
            setArticle(mockArticle);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (slug) {
      loadArticle();
    }
    
    return () => {
      isMounted = false;
    };
  }, [slug]);

  return { article, loading, error };
};
